import {useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {UseFormReturn} from 'react-hook-form';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {IPresentationTheme} from '../../../incase-domain/presentationTheme';
import {fetchPresentationThemeListService} from '../../../service/presentationTheme.service';
import Button from '../../../incase-domain/components/button/Button';
import {PresentationSettingsFormType} from '../../../incase-domain/validators/presentation/presentationSchema';
import PresentationBackgroundSettingsForm from './PresentationBackgroundSettingsForm';
import Label from '../../../incase-domain/components/label/Label';
import DropdownMenuCompact from '../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import ChecklistTile from '../../../incase-domain/components/checkbox/ChecklistTile';
import {showGridSelector} from '../../../store/selectors/grid';
import {switchGridAction} from '../../../store/actions/grid';

interface PresentationThemeSettingsFormProps {
    useForm: UseFormReturn<PresentationSettingsFormType>;
}

export default function PresentationThemeSettingsForm({useForm}: PresentationThemeSettingsFormProps): JSX.Element {
    const {watch} = useForm;
    const presentation = watch('presentation');

    const {t} = useTranslation();
    const dispatch = useDispatch();
    const client = useQueryClient();
    const showGrid = useSelector(showGridSelector);

    const handleSelectTheme = (presentationThemeId: string): void => {
        const updatedPresentation = {
            ...presentation,
            presentationThemeId,
        };

        useForm.setValue('presentation', updatedPresentation);
        client.invalidateQueries({queryKey: QUERY_KEYS.theme.lists()});
    };

    const presentationThemeList: UseQueryResult<IPresentationTheme[]> = useQuery<IPresentationTheme[]>(QUERY_KEYS.theme.lists(), () =>
        fetchPresentationThemeListService()
    );

    return (
        <>
            <div className="c-verticalNavigation__header">
                <div className="c-verticalNavigation__header_title">{t('presEditPage.settings.themes.title')}</div>
            </div>
            <div>
                <>
                    <Label>{t('presEditPage.themeModal.themeLabel')}</Label>
                    <DropdownMenuCompact
                        width="full"
                        items={[
                            [
                                {
                                    label: t('presEditPage.themeModal.noTheme'),
                                    onClick: () => handleSelectTheme(''),
                                },
                            ],
                            presentationThemeList.data
                                ?.filter((el) => el._id)
                                .map((el) => ({
                                    label: el.themeName,
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    onClick: () => handleSelectTheme(el._id!),
                                })) || [],
                        ]}
                        className="c-sidepanel__dropdown"
                    >
                        <Button
                            type="button"
                            fullWidth
                            size="sm"
                            trailing="solar:alt-arrow-down-linear"
                            variant="soft"
                            className="c-sidepanel__dropdown_button"
                        >
                            {presentationThemeList.data?.find((el) => el._id === presentation.presentationThemeId)?.themeName ||
                                t('presEditPage.themeModal.noTheme')}
                        </Button>
                    </DropdownMenuCompact>
                </>
            </div>

            <div className="c-verticalNavigation__section">{t('presEditPage.themeModal.background')}</div>

            <PresentationBackgroundSettingsForm useForm={useForm} />

            <div className="c-verticalNavigation__section">{t('presEditPage.themeModal.other')}</div>

            <div className="l-group c-themeModal__section">
                <ChecklistTile value={showGrid} label={t('presEditPage.themeModal.grid')} onChange={() => dispatch(switchGridAction())} />
            </div>
        </>
    );
}
